// THE SATELLITE VIEW OF THE OWNER'S PROPERTY ON STEP 1 — APPLE MAPKIT JS (WEB-PARITY-1C, DT-2).
//
// The app's step 1 shows `SatelliteMapCard` (AppealAce/Shared/SatelliteMapCard.swift): Apple's satellite imagery,
// 160 m across (SMC:106-108), centred on the property with an amber pin (SMC:126-132), 190 points tall (SMC:13), and it
// disappears — no box, no words — when it cannot draw (SMC:55-56). Tyler, 2026-09-29: "Property details should have the
// image of the home". D2 ruled satellite, as the app's step 1; the privacy sentence for it is approved ("On
// app.proppeal.com, the satellite view of your property comes from Apple Maps.").
//
// The app uses MapKit's snapshotter, which MapKit JS does not have; the same imagery is drawn here as a MapKit JS map
// that cannot be moved, zoomed or rotated, with no controls, which is the app's still picture in everything the owner
// can see.
//
// WHAT IT SENDS, AND TO WHOM.
//   · /api/mapkit-token (this site's own function) — the same request the address box makes (`requestMapKitToken`),
//     carrying nothing about the owner. With no MapKit key on the project it answers so, and there is no image.
//   · Apple — MapKit JS (`MAPKIT_SRC`, the address box's script and loader), then the map tiles around the property's
//     coordinates, which is what the approved privacy sentence says.
//
// ANY FAILURE IS SILENT: no key, a script that will not load, MapKit JS's own error, or no map after `DRAW_TIMEOUT_MS`.
// The caller removes the card. Nothing is retried on this page.

import { initMapKit, loadMapKitScript, requestMapKitToken, type MapKit, type TokenResponse } from "./addressSuggest";

/** The app's snapshot region: 160 m on a side (SatelliteMapCard.swift:106-108). */
export const SPAN_METERS = 160;
/** The app's card height, in points (SatelliteMapCard.swift:13); the web card is this many CSS pixels. */
export const CARD_HEIGHT = 190;
/** The app's pin: `mappin.circle.fill` at 34 points (SatelliteMapCard.swift:126-127). */
export const PIN_SIZE = 34;
/** How long MapKit JS gets to load and draw before the card is taken away. */
export const DRAW_TIMEOUT_MS = 15_000;
/** Metres in one degree of latitude. */
const METERS_PER_DEGREE = 111_320;

export interface Coordinates {
  readonly latitude: number;
  readonly longitude: number;
}

export interface SatelliteView {
  /** Whether this page can draw a satellite view at all: our token function has a MapKit key to sign with. */
  available(): Promise<boolean>;
  /**
   * Draws the view of `at` into `el`. Resolves with a function that takes the map down, or rejects when it cannot be
   * drawn. `onFail` is called if MapKit JS fails after that (a refused token, for one).
   */
  draw(el: HTMLElement, at: Coordinates, onFail: () => void): Promise<() => void>;
}

/** The region that fills a `width` × `height` box the way the app's square snapshot fills its card (scaled to fill,
 *  SatelliteMapCard.swift:27): the longer side spans `SPAN_METERS`, the shorter side proportionally less. */
export function regionSpan(at: Coordinates, width: number, height: number): { latitudeDelta: number; longitudeDelta: number } {
  const w = width > 0 ? width : 1;
  const h = height > 0 ? height : 1;
  const acrossMeters = w >= h ? SPAN_METERS : (SPAN_METERS * w) / h;
  const downMeters = h >= w ? SPAN_METERS : (SPAN_METERS * h) / w;
  const cos = Math.cos((at.latitude * Math.PI) / 180);
  return {
    latitudeDelta: downMeters / METERS_PER_DEGREE,
    longitudeDelta: acrossMeters / (METERS_PER_DEGREE * (Math.abs(cos) > 1e-6 ? Math.abs(cos) : 1e-6)),
  };
}

// ── what MapKit JS's map library hands back, as far as this module reads it ─────────────────────────────────────────

interface MapKitMap {
  addAnnotation(annotation: unknown): unknown;
  destroy(): void;
}
interface MapKitWithMaps extends MapKit {
  Map?: (new (el: HTMLElement, options: Record<string, unknown>) => MapKitMap) & { MapTypes?: { Satellite?: string } };
  CoordinateRegion?: new (center: unknown, span: unknown) => unknown;
  CoordinateSpan?: new (latitudeDelta: number, longitudeDelta: number) => unknown;
  Annotation?: new (at: unknown, factory: () => Element, options?: Record<string, unknown>) => unknown;
  FeatureVisibility?: { Hidden?: string };
  removeEventListener?(type: string, listener: () => void): void;
}

/** The app's pin, drawn: an amber disc with a white pin in it, its foot on the property (SatelliteMapCard.swift:129-132). */
export function pinElement(doc: Document): Element {
  const ns = "http://www.w3.org/2000/svg";
  const svg = doc.createElementNS(ns, "svg");
  svg.setAttribute("width", String(PIN_SIZE));
  svg.setAttribute("height", String(PIN_SIZE));
  svg.setAttribute("viewBox", "0 0 34 34");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("class", "satellite-pin");
  const disc = doc.createElementNS(ns, "circle");
  disc.setAttribute("cx", "17");
  disc.setAttribute("cy", "17");
  disc.setAttribute("r", "16");
  disc.setAttribute("fill", "#fca311");
  disc.setAttribute("stroke", "#ffffff");
  disc.setAttribute("stroke-width", "1.5");
  const pin = doc.createElementNS(ns, "path");
  pin.setAttribute("d", "M17 8.5a5.5 5.5 0 0 0-5.5 5.5c0 4.1 5.5 10.5 5.5 10.5s5.5-6.4 5.5-10.5A5.5 5.5 0 0 0 17 8.5Zm0 7.6a2.1 2.1 0 1 1 0-4.2 2.1 2.1 0 0 1 0 4.2Z");
  pin.setAttribute("fill", "#ffffff");
  svg.appendChild(disc);
  svg.appendChild(pin);
  return svg;
}

export interface SatelliteDeps {
  fetch(url: string, init: RequestInit): Promise<TokenResponse>;
  loadMapKit(): Promise<MapKit>;
  now?(): number;
  setTimeout(f: () => void, ms: number): unknown;
  clearTimeout(t: unknown): void;
  document: Document;
}

export function createSatelliteView(deps: SatelliteDeps): SatelliteView {
  let spare: { token: string; expiresAt: number } | null = null;
  let availability: Promise<boolean> | null = null;
  const nowSeconds = (): number => Math.floor((deps.now ? deps.now() : Date.now()) / 1000);

  function available(): Promise<boolean> {
    if (availability === null) {
      availability = requestMapKitToken(deps.fetch).then(
        (t) => {
          spare = t;
          return true;
        },
        () => false,
      );
    }
    return availability;
  }

  /** MapKit JS's `authorizationCallback`: the token `available` fetched while it is fresh, then a new one each time. */
  function authorize(done: (token: string) => void): void {
    const t = spare;
    spare = null;
    if (t && t.expiresAt - nowSeconds() > 60) {
      done(t.token);
      return;
    }
    requestMapKitToken(deps.fetch).then((fresh) => done(fresh.token), () => undefined);
  }

  async function drawNow(el: HTMLElement, at: Coordinates, onFail: () => void): Promise<() => void> {
    if (!(await available())) throw new Error("no MapKit key");
    const mapkit = (await deps.loadMapKit()) as MapKitWithMaps;
    if (!mapkit || typeof mapkit.init !== "function") throw new Error("MapKit JS loaded without its API");
    initMapKit(mapkit, authorize);
    let failed = false;
    const fail = (): void => {
      if (failed) return;
      failed = true;
      onFail();
    };
    if (typeof mapkit.addEventListener === "function") mapkit.addEventListener("error", fail);
    if (typeof mapkit.load === "function") await mapkit.load(["map", "annotations"]);
    const { Map, Coordinate, CoordinateRegion, CoordinateSpan, Annotation } = mapkit;
    if (!Map || !Coordinate || !CoordinateRegion || !CoordinateSpan || !Annotation) throw new Error("no map library");
    const center = new Coordinate(at.latitude, at.longitude);
    const span = regionSpan(at, el.clientWidth, el.clientHeight || CARD_HEIGHT);
    const hidden = mapkit.FeatureVisibility?.Hidden ?? "hidden";
    // A STILL PICTURE, as the app's is: nothing moves, nothing zooms, no control is drawn.
    const map = new Map(el, {
      mapType: Map.MapTypes?.Satellite ?? "satellite",
      region: new CoordinateRegion(center, new CoordinateSpan(span.latitudeDelta, span.longitudeDelta)),
      isScrollEnabled: false,
      isZoomEnabled: false,
      isRotationEnabled: false,
      isPitchEnabled: false,
      showsCompass: hidden,
      showsScale: hidden,
      showsMapTypeControl: false,
      showsZoomControl: false,
      showsUserLocationControl: false,
      showsPointsOfInterest: false,
    });
    // Its foot on the property: a MapKit JS annotation is anchored at its element's bottom centre, as the app draws
    // its pin above the point (SatelliteMapCard.swift:129-132).
    map.addAnnotation(new Annotation(center, () => pinElement(deps.document), { calloutEnabled: false, enabled: false }));
    return () => {
      if (typeof mapkit.removeEventListener === "function") mapkit.removeEventListener("error", fail);
      try {
        map.destroy();
      } catch {
        /* already gone */
      }
    };
  }

  function draw(el: HTMLElement, at: Coordinates, onFail: () => void): Promise<() => void> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = deps.setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error("MapKit JS took too long"));
      }, DRAW_TIMEOUT_MS);
      drawNow(el, at, onFail).then(
        (takeDown) => {
          deps.clearTimeout(timer);
          if (settled) {
            takeDown();
            return;
          }
          settled = true;
          resolve(takeDown);
        },
        (error: unknown) => {
          deps.clearTimeout(timer);
          if (settled) return;
          settled = true;
          reject(error instanceof Error ? error : new Error("MapKit JS failed"));
        },
      );
    });
  }

  return { available, draw };
}

/** The satellite view for this page load, on the browser's own fetch, timers and the address box's script loader. */
export function createBrowserSatellite(): SatelliteView | null {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return null;
  return createSatelliteView({
    fetch: (url, init) => window.fetch(url, init),
    loadMapKit: loadMapKitScript,
    now: () => Date.now(),
    setTimeout: (f, ms) => window.setTimeout(f, ms),
    clearTimeout: (t) => window.clearTimeout(t as number),
    document: window.document,
  });
}
